import { Button, Checkbox } from '@consigli/facade';
import { type ProjectParticipant, type ResponsibleParty } from '@consigli/types';
import { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPencilAlt, FaQuoteRight, FaTrash } from 'react-icons/fa';

import {
  isProjectActionItem,
  type ActionItemColumn,
  type GroupByOption,
  type SupportedItem,
} from './action-item-types';
import {
  ACTIONS_COLUMN_WIDTH,
  formatDateToLocale,
  getColumnWidth,
  isGenericSpeakerName,
} from './action-items-table-utils';
import { DeleteConfirmModal } from './delete-confirm-modal';
import { EditableDeadline } from './editable-deadline';
import { EditableDescription, type EditableDescriptionHandle } from './editable-description';
import { EditableStatus } from './editable-status';
import { EditableTopic } from './editable-topic';
import { ReassignDropdown } from './reassign-dropdown';
import { Avatar } from '../utils/participant-utils';

export interface ActionItemTableRowProps {
  item: SupportedItem;
  columns: ActionItemColumn[];
  groupBy: GroupByOption;
  participants: ProjectParticipant[];
  customParties?: ResponsibleParty[];
  topics: string[];
  meetingId?: string;
  onUpdate?: (item: SupportedItem) => void;
  onDelete: (item: SupportedItem) => void;
  onAddCustomTopic?: (topic: string) => void;
  onShowSource?: (item: SupportedItem) => void;
  // Selection props
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

export const ActionItemTableRow: FC<ActionItemTableRowProps> = ({
  item,
  columns,
  groupBy,
  participants,
  customParties,
  topics,
  meetingId,
  onUpdate,
  onDelete,
  onAddCustomTopic,
  onShowSource,
  isSelected,
  onToggleSelect,
}) => {
  const { t, i18n } = useTranslation();
  const editRef = useRef<EditableDescriptionHandle>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const sourceSegmentIds = item.sourceSegmentIds ?? [];
  const canShowSource = sourceSegmentIds.length > 0 && !!onShowSource;

  // Calculate visible columns (exclude the grouped column)
  const visibleColumns = useMemo(
    () => columns.filter((col) => col !== groupBy),
    [columns, groupBy],
  );

  const handleDeleteConfirm = () => {
    setIsDeleteModalOpen(false);
    onDelete(item);
  };

  // Show checkbox for person grouping when selection is available
  const showCheckbox = groupBy === 'person' && !!onToggleSelect;

  return (
    <>
      <div className="flex gap-4 px-4 py-3 pl-12 hover:bg-day-light-3 text-sm items-start group">
        {showCheckbox && (
          <div className="flex items-center pt-0.5">
            <Checkbox
              id={`select-item-${item.id}`}
              checked={isSelected ?? false}
              onChange={() => onToggleSelect?.()}
              ariaLabel={t('meeting-secretary.action-items.select-item', {
                description: item.descriptionText,
                defaultValue: `Select action item: ${item.descriptionText}`,
              })}
            />
          </div>
        )}
        {visibleColumns.map((col) => {
          switch (col) {
            case 'title':
              return (
                <div key={col} className={getColumnWidth(col)}>
                  <EditableDescription
                    item={item}
                    meetingId={meetingId}
                    ref={editRef}
                    onUpdate={onUpdate}
                  />
                </div>
              );
            case 'person': {
              const personName = item.responsiblePerson?.name ?? null;
              let personDisplay = personName ?? '—';
              if (personName && isGenericSpeakerName(personName)) {
                const num = personName.match(/\d+/)?.[0];
                const prefix = t('meeting-secretary.action-items.speaker-prefix');
                if (num) personDisplay = `${prefix} ${num}`;
              }
              return (
                <div
                  key={col}
                  className={`${getColumnWidth(col)} text-gray-600 flex items-center gap-2 min-w-0`}
                  title={personDisplay}
                >
                  {personName ? (
                    <>
                      <Avatar name={personName} className="!h-6 !w-6 !text-[10px] !ring-0" />
                      <span className="truncate">{personDisplay}</span>
                    </>
                  ) : (
                    <span>{personDisplay}</span>
                  )}
                </div>
              );
            }
            case 'deadline': {
              return (
                <div key={col} className={`${getColumnWidth(col)} text-gray-600`}>
                  <EditableDeadline item={item} meetingId={meetingId} onUpdate={onUpdate} />
                </div>
              );
            }
            case 'topic': {
              return (
                <div key={col} className={`${getColumnWidth(col)} text-gray-600`}>
                  <EditableTopic
                    item={item}
                    meetingId={meetingId}
                    topics={topics}
                    onUpdate={onUpdate}
                    onAddCustomTopic={onAddCustomTopic}
                  />
                </div>
              );
            }
            case 'status':
              return (
                <div key={col} className={getColumnWidth(col)}>
                  <EditableStatus item={item} meetingId={meetingId} onUpdate={onUpdate} />
                </div>
              );
            case 'meeting': {
              // Meeting column is only used in project-level view where all items are ProjectActionItem
              // Use type guard to safely access project-level fields
              const meetingTitle = isProjectActionItem(item) ? item.meetingTitle : null;
              const meetingDate = isProjectActionItem(item) ? item.meetingScheduledStartTime : null;
              return (
                <div key={col} className={`${getColumnWidth(col)} text-gray-600`}>
                  <div className="flex flex-col">
                    <span>{meetingTitle || '—'}</span>
                    {meetingDate && (
                      <span className="text-xs text-gray-400">
                        {formatDateToLocale(meetingDate, i18n.language)}
                      </span>
                    )}
                  </div>
                </div>
              );
            }
            default:
              return null;
          }
        })}

        {/* Actions column. Stays visible while the reassign popover (role="dialog")
            is open so moving the mouse off the row doesn't hide it mid-edit. */}
        <div
          className={`${ACTIONS_COLUMN_WIDTH} flex justify-end items-center gap-0.5 opacity-0 group-hover:opacity-100 has-[[role=dialog]]:opacity-100 transition-all`}
        >
          {canShowSource && (
            <Button
              tertiary
              iconOnly
              icon={FaQuoteRight}
              iconSize={14}
              onClick={(e) => {
                e.stopPropagation();
                onShowSource?.(item);
              }}
              className="!p-2 rounded-full text-gray-400 hover:text-brand hover:bg-day-light-3 transition-colors"
              title={t('meeting-secretary.meetings.view-transcript-source')}
              aria-label={t('meeting-secretary.meetings.view-transcript-source')}
            />
          )}
          <Button
            tertiary
            iconOnly
            icon={FaPencilAlt}
            iconSize={14}
            onClick={(e) => {
              e.stopPropagation();
              editRef.current?.startEditing();
            }}
            className="!p-2 rounded-full text-gray-400 hover:text-brand hover:bg-day-light-3 transition-colors"
            title={t('meeting-secretary.meetings.edit-action-item')}
          />
          <ReassignDropdown
            item={item}
            meetingId={meetingId}
            participants={participants}
            customParties={customParties}
            onUpdate={onUpdate}
          />
          <Button
            tertiary
            iconOnly
            icon={FaTrash}
            iconSize={14}
            onClick={(e) => {
              e.stopPropagation();
              setIsDeleteModalOpen(true);
            }}
            className="!p-2 rounded-full text-gray-400 hover:text-error-dark hover:bg-day-light-3 transition-colors"
            title={t('meeting-secretary.meetings.delete-action-item')}
          />
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        titleKey="meeting-secretary.meetings.delete-action-item"
        messageKey="meeting-secretary.meetings.confirm-delete-action-item"
      />
    </>
  );
};
